import type { OSCategory, OSType } from "@/types/admin";
import { createTableFiltersStore } from "./createTableFiltersStore";

/** Categories the list filters by — the values stored on the row. `All` is the cleared state. */
export const OS_IMAGE_CATEGORY_OPTIONS: {
  value: OSCategory | "All";
  label: string;
}[] = [
  { value: "All", label: "All Categories" },
  { value: "Linux", label: "Linux" },
  { value: "Windows", label: "Windows" },
  { value: "BSD", label: "BSD" },
  { value: "Custom", label: "Custom" },
];

/** OS types the backend derives from image metadata; sent as-is to the `osType` parameter. */
export const OS_IMAGE_TYPE_OPTIONS: { value: OSType | "All"; label: string }[] = [
  { value: "All", label: "All OS Types" },
  { value: "ubuntu", label: "Ubuntu" },
  { value: "debian", label: "Debian" },
  { value: "rocky", label: "Rocky Linux" },
  { value: "alma", label: "AlmaLinux" },
  { value: "centos", label: "CentOS" },
  { value: "fedora", label: "Fedora" },
  { value: "windows", label: "Windows" },
  { value: "windows-server", label: "Windows Server" },
  { value: "freebsd", label: "FreeBSD" },
  { value: "openbsd", label: "OpenBSD" },
];

export const OS_IMAGE_STATUS_OPTIONS: {
  value: "Active" | "Inactive" | "All";
  label: string;
}[] = [
  { value: "All", label: "All Statuses" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

export interface OSImageListFilters extends Record<string, unknown> {
  /** Free-text term matched against the image name, template name and description. */
  search: string;
  category: OSCategory | "All";
  osType: OSType | "All";
  status: "Active" | "Inactive" | "All";
}

const defaults: OSImageListFilters = {
  search: "",
  category: "All",
  osType: "All",
  status: "All",
};

const isOffered = <T,>(options: { value: T }[], value: unknown): value is T =>
  options.some((option) => option.value === value);

/**
 * Filter and paging state for the admin OS image list, cached in sessionStorage.
 *
 * No `defaultSort`: the screen has no sort headers, so the page sends no sortBy/sortOrder and the
 * backend's own catalog order (by name) applies — the order customers see.
 *
 * Bump `version` in step with any change to the shape above or to the option lists.
 */
export const useOSImageFiltersStore = createTableFiltersStore<OSImageListFilters>({
  name: "admin.osImages.filters",
  version: 1,
  defaults,
  sanitize: (filters) => ({
    ...filters,
    // sessionStorage outlives a release: a value no longer offered must not survive as a filter the
    // operator cannot see in the UI, and so cannot clear from it.
    category: isOffered(OS_IMAGE_CATEGORY_OPTIONS, filters.category)
      ? filters.category
      : defaults.category,
    osType: isOffered(OS_IMAGE_TYPE_OPTIONS, filters.osType)
      ? filters.osType
      : defaults.osType,
    status: isOffered(OS_IMAGE_STATUS_OPTIONS, filters.status)
      ? filters.status
      : defaults.status,
  }),
});
